/**
 * The bookkeeping every room planner shares: minting entries, remembering each
 * one's place in the programme for the budget, and reporting what was dropped.
 */

const { stagingEntry } = require('./entry');
const { attributesFor } = require('./attributes');

/**
 * @param {Object} room
 * @param {Object} style  a resolved style pack
 * @param {Object} opts   the planner's opts: idPrefix, generatedAt, onDrop
 * @param {Object[]} program  the room's programme, in precedence order
 */
function createMinter(room, style, opts, program) {
  const idPrefix = opts.idPrefix || 'staging';
  // Every prop this room wanted and could not have. `placeOnSurface` returns
  // `rejected` precisely so the caller can account for it, and the caller used
  // to destructure it away — which made "the counter had no room" and "the
  // programme never asked for one" indistinguishable from the outside.
  const onDrop = typeof opts.onDrop === 'function' ? opts.onDrop : null;
  const drop = (type, reason) => {
    if (onDrop) onDrop({ type, reason, roomId: room.id || null });
  };
  const styleId = style?.id || null;
  const generatedAt = opts.generatedAt || null;

  // Ids are descriptive and planner-local; the persisting caller re-mints them,
  // which is exactly why the art seed is stamped rather than derived from one.
  let seq = 0;
  const make = (type, fields) =>
    stagingEntry({
      id: `${idPrefix}-${type}-${seq++}`,
      type,
      styleId,
      generatedAt,
      attributes: attributesFor(type, style),
      ...fields,
    });

  /**
   * What each minted prop's place in the PROGRAMME was — its rank and whether
   * it was declared droppable. Kept beside the entries rather than on them,
   * because `stagingEntry` is the persisted record and this is planning
   * bookkeeping that must never reach Firestore.
   *
   * It exists because the cap is spent at the END (see `trimToBudget`), and by
   * then an entry no longer knows which programme line asked for it.
   * `staging-towel-bath` is the case that makes a type-keyed lookup wrong: it
   * is not a programme entry at all, it is the substitution the bar line makes
   * when a ring has already placed, so its precedence is the BAR's.
   */
  const curation = new Map();
  const rankOf = new Map(program.map((p, i) => [p.type, i]));
  const mint = (prop, type, fields) => {
    const entry = make(type, fields);
    curation.set(entry, {
      rank: rankOf.has(prop.type) ? rankOf.get(prop.type) : program.length,
      optional: !!prop.optional,
    });
    return entry;
  };

  const wantedOf = (where) =>
    program
      .filter((p) => p.where === where)
      .flatMap((p) =>
        Array.from({ length: Math.max(0, Math.floor(p.count || 0)) }, () => p)
      );

  return { make, mint, curation, drop, wantedOf };
}

module.exports = { createMinter };
